import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { kayshFlushOperator, kayshOperator } from '@karacas/kaysh';
import { _aux_getEnumStringByIdPromotionTypes } from '@prisma/components/generic-select-promotion-types/services/generic-select-promotion-types.service';
import { _isDev, _log, _logTap } from '@shared/aux_helper_environment';
import { _get, _ms } from '@shared/aux_helper_functions';
import { PagedList } from 'core/models/paged-list.model';
import { Observable, of } from 'rxjs';
import { catchError, delay, tap, map } from 'rxjs/operators';
import { MessageStructuresListFiltersModel, MessageStructuresModel } from '../models/messageStructuresList.models';
import { _mapDummyDataToPagedList, DUMMY_MESSAGE_STRUCTURES } from './messageStructuresList-dummy';

@Injectable({
  providedIn: 'root',
})
export class MessageStructuresListService {
  private configBase = _get(environment, 'apiBaseUrl_promotionV2');
  private actionConfig = environment.promotionV2.configuration.api;

  constructor(private http: HttpClient) {}

  private USE_DUMMY_DATA = false;

  getPromotionalMessageList(filters: any, includeSelectedChips: boolean = false): Observable<PagedList<MessageStructuresModel>> {
    if (this.USE_DUMMY_DATA) {
      return of(_mapDummyDataToPagedList(DUMMY_MESSAGE_STRUCTURES, includeSelectedChips, filters.communicationType)).pipe(delay(1000));
    }
    let _url = `${this.configBase}${this.actionConfig.resources.getPromotionalMessageList}`;
    const params = { query: filters?.query || null, pageIndex: filters?.pageIndex || 0, pageSize: filters?.pageSize || 10 };
    const _filtersToBe = _mapFiltersToBe(filters);

    const searchParams = new URLSearchParams(
      Object.entries(params)
        .filter(([_, value]) => value != null)
        .map(([key, value]) => [key, value.toString()])
    );

    if (searchParams.toString()) {
      _url = `${_url}?${searchParams.toString()}`;
    }

    return this.http.post<PagedList<MessageStructuresModel>>(_url, { ..._filtersToBe, includeSelectedChips }).pipe(
      tap(data => _logTap(`${MessageStructuresListService.name}::getPromotionalMessageList (tap)\n\tdata: %o`, data)),
      map(data => ({
        ...data,
        items: data.items.sort((a, b) => {
          if (a.isDefaultMessageStructure && !b.isDefaultMessageStructure) return -1;
          if (!a.isDefaultMessageStructure && b.isDefaultMessageStructure) return 1;
          return a.id - b.id;
        }),
      })),
      kayshOperator('getPromotionalMessageList', _filtersToBe, { localStorage: false, maxTime: _ms('3s') }),
      catchError(error => {
        _log(['getPromotionalMessageList', 'error'], error);
        if (true && _isDev()) {
          return of(_mapDummyDataToPagedList(DUMMY_MESSAGE_STRUCTURES, includeSelectedChips, filters.communicationType));
        }
        return of(null);
      })
    );
  }

  promotionalMessageListResetCache() {
    return kayshFlushOperator('getPromotionalMessageList');
  }

  cancelPromotionalMessage(structureId: number, newDefaultStructureId?: number): Observable<void> {
    let _url = `${this.configBase}${this.actionConfig.resources.cancelPromotionalMessage}/${structureId}`;
    if (newDefaultStructureId) {
      _url += `/${newDefaultStructureId}`;
    }
    return this.http.delete<void>(_url).pipe(
      tap(() => _logTap(`${MessageStructuresListService.name}::cancelPromotionalMessage - success`)),
      kayshOperator('cancelPromotionalMessage', structureId, { localStorage: false, maxTime: _ms('10s') })
    );
  }

  clonePromotionalMessage(data: any): Observable<any> {
    const url = `${this.configBase}${this.actionConfig.resources.clonePromotionalMessage}`;
    return this.http.post<any>(url, data).pipe(
      tap(response => _logTap(`${MessageStructuresListService.name}::clonePromotionalMessage (tap)\n\tresponse: %o`, response)),
      catchError(error => {
        _log(['clonePromotionalMessage', 'error'], error);
        return of(null);
      })
    );
  }
}

const _mapFiltersToBe = (filters): MessageStructuresListFiltersModel => {
  return {
    ...filters,
    communicationType: filters.communicationType || null,
    promotionType:
      Array.isArray(filters?.promotionType) && filters?.promotionType.length > 0
        ? filters?.promotionType?.map(p => _aux_getEnumStringByIdPromotionTypes(p, false))?.[0]
        : filters?.promotionType,
  };
};
